import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, TextField, Button, CircularProgress, Alert, Grid,
    FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle,
    DialogContent, DialogActions, Paper, LinearProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { ja } from 'date-fns/locale';
import {
    fetchOrganizations,
    fetchAuthorsByOrganization,
    fetchBiliVideoParts,
    createClip,
    uploadSubtitle // 导入新增的函数
} from '../api/apiService';
import SendIcon from '@mui/icons-material/Send';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';


// (保留原有的 liveFormatMap 和 liveContentMap)
const liveFormatMap = { 1: '3D', 2: '2D', 3: '电台', 4: '联动', 5: '双播', 6: '其它' };
const liveContentMap = { 1: '才艺', 2: '杂谈', 3: '游戏', 4: '综艺', 5: '活动', 6: '观影', 7: '特殊', 8: '投稿', 9: '歌回', 10: '其它' };


const BiliBiliToolPage = () => {
    const [biliInput, setBiliInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    const [commonData, setCommonData] = useState({
        organizationId: '', authorId: '', live_format: 6,
        live_content: 10, datetime: new Date(),
    });
    const [parts, setParts] = useState([]);
    
    const [organizations, setOrganizations] = useState([]);
    const [authors, setAuthors] = useState([]); // 这个是关键状态，必须和 commonData.organizationId 同步
    
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewPayload, setPreviewPayload] = useState(null);
    const [submissionStatus, setSubmissionStatus] = useState({ msg: '', type: '' });

    const [createdClips, setCreatedClips] = useState([]); 
    const [subtitleUploadStep, setSubtitleUploadStep] = useState(false); 
    const [subtitleUploadStatus, setSubtitleUploadStatus] = useState({ msg: '', type: '' });
    const [subtitleUploadProgress, setSubtitleUploadProgress] = useState(0);

    // 字幕编辑弹窗状态
    const [subtitleEditOpen, setSubtitleEditOpen] = useState(false);
    const [editingPartId, setEditingPartId] = useState(null);
    const [editingSubtitle, setEditingSubtitle] = useState('');

    const orgAuthors = useMemo(() =>
        authors.filter(a => a.organizationId === commonData.organizationId),
        [authors, commonData.organizationId]
    );

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const orgs = await fetchOrganizations();
                setOrganizations(orgs);
                if (orgs.length > 0) {
                    const orgId = orgs[0].id;
                    await handleOrgChange({ target: { value: orgId } });
                }
            } catch (err) { setError("无法加载社团或作者列表"); }
        };
        fetchInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const handleOrgChange = async (event) => {
        const orgId = event.target.value;
        setCommonData(prev => ({ ...prev, organizationId: orgId, authorId: '' })); // 1. 设置Org，清空Author
        try {
            const authorsData = await fetchAuthorsByOrganization(orgId);
            setAuthors(authorsData); // 2. 更新作者列表状态 (关键)
             if (authorsData.length > 0) {
                const defaultDate = new Date();
                defaultDate.setSeconds(0,0);
                // 3. 再次设置State，填入默认作者和默认时间
                setCommonData(prev => ({ 
                    ...prev, 
                    organizationId: orgId, // 确保 orgId 覆盖
                    authorId: authorsData[0].id, // 设置默认作者
                    datetime: defaultDate // 设置默认时间
                }));
            }
        } catch (err) { 
            setError("无法加载该社团的作者列表"); 
            setAuthors([]); // 清空作者列表
        }
    };

    const handleParse = async () => {
        const bvidMatch = biliInput.match(/BV[1-9A-HJ-NP-Za-km-z]{10}/i);
        if (!bvidMatch) {
            setError("未能识别出有效的BV号，请检查输入");
            return;
        }
        const bvid = bvidMatch[0];

        setLoading(true);
        setError('');
        setParts([]);
        try {
            const data = await fetchBiliVideoParts(bvid);
            // 处理封面链接，去除 http:// 或 https:// 前缀
            setParts(data.map(p => ({
                ...p,
                id: p.part,
                cover: p.cover ? p.cover.replace(/^https?:\/\//i, '') : ''
            })));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePartDataChange = (partId, field, value) => {
        setParts(prevParts => prevParts.map(p => 
            p.id === partId ? { ...p, [field]: value } : p
        ));
    };
    
    const handleDateChange = (newDate) => {
        if (newDate instanceof Date && !isNaN(newDate)) {
            newDate.setSeconds(0, 0);
        }
        setCommonData(prev => ({ ...prev, datetime: newDate }));
    };

    const resetAllState = () => {
        setBiliInput('');
        setParts([]);
        setPreviewOpen(false);
        setPreviewPayload(null);
        setSubmissionStatus({ msg: '', type: '' });
        setCreatedClips([]);
        setSubtitleUploadStep(false);
        setSubtitleUploadStatus({ msg: '', type: '' });
        setSubtitleUploadProgress(0);
        setSubtitleEditOpen(false);
        setEditingPartId(null);
        setEditingSubtitle('');
    };

    // 打开字幕编辑弹窗
    const handleOpenSubtitleEdit = (partId) => {
        const part = parts.find(p => p.id === partId);
        setEditingPartId(partId);
        setEditingSubtitle(part?.subtitleSrt || '');
        setSubtitleEditOpen(true);
    };

    // 保存字幕编辑
    const handleSaveSubtitleEdit = () => {
        setParts(prevParts => prevParts.map(p =>
            p.id === editingPartId ? { ...p, subtitleSrt: editingSubtitle } : p
        ));
        setSubtitleEditOpen(false);
        setEditingPartId(null);
        setEditingSubtitle('');
    };


    const handlePreview = () => {
        if (parts.length === 0 || !commonData.authorId) {
            alert("请先解析视频并选择作者！"); return;
        }
        const formatLocalDateTime = (date) => {
            const pad = (num) => num.toString().padStart(2, '0');
            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
        };
        const payload = parts.map(p => ({
            authorId: commonData.authorId,
            bv: biliInput.match(/BV[1-9A-HJ-NP-Za-km-z]{10}/i)[0],
            title: p.title,
            datetime: formatLocalDateTime(new Date(commonData.datetime)),
            cover: p.cover,
            part: p.part,
            type: 1,
            live_format: commonData.live_format,
            live_content: commonData.live_content
        }));
        setPreviewPayload(payload);
        setPreviewOpen(true);
    };

    const handleConfirmUpload = async () => {
        setSubmissionStatus({ msg: '正在上传视频信息...', type: 'info' });
        const newClips = [];
        try {
            for (let i = 0; i < previewPayload.length; i++) {
                const clipData = previewPayload[i];
                const createdClip = await createClip(clipData);
                // 后端可能返回完整 clip 对象，或者返回 { lastInsertRowid: id }
                // 为了兼容，我们从原始数据中获取 part 信息
                const partData = parts.find(p => p.part === clipData.part);
                newClips.push({ 
                    ...createdClip, 
                    part: clipData.part, // 确保 part 存在
                    subtitleSrt: partData?.subtitleSrt 
                });
            }
            setCreatedClips(newClips);
            setSubmissionStatus({ msg: `共 ${newClips.length} 个分P的视频信息全部新增成功！`, type: 'success' });
            setSubtitleUploadStep(true);
        } catch (err) {
            setSubmissionStatus({ msg: `视频信息上传失败: ${err.message}`, type: 'error' });
        }
    };
    
    const handleConfirmSubtitleUpload = async () => {
        const clipsWithSubtitles = createdClips.filter(c => c.subtitleSrt && c.subtitleSrt.trim() !== '');
        if (clipsWithSubtitles.length === 0) {
            setSubtitleUploadStatus({ msg: '没有需要上传的字幕。操作完成！', type: 'info' });
            setTimeout(() => {
                setPreviewOpen(false);
                resetAllState();
            }, 2000);
            return;
        }

        setSubtitleUploadStatus({ msg: '开始上传字幕...', type: 'info' });
        let successCount = 0;

        for (let i = 0; i < clipsWithSubtitles.length; i++) {
            const clip = clipsWithSubtitles[i];
            try {
                await uploadSubtitle(
                    clip.id, 
                    clip.subtitleSrt
                );
                successCount++;
                setSubtitleUploadProgress(((i + 1) / clipsWithSubtitles.length) * 100);
            } catch (err) {
                setSubtitleUploadStatus({ msg: `P${clip.part} 的字幕上传失败: ${err.message}`, type: 'error' });
                return;
            }
        }

        setSubtitleUploadStatus({ msg: `共 ${successCount} 个字幕全部上传成功！`, type: 'success' });
        setTimeout(() => {
            setPreviewOpen(false);
            resetAllState();
        }, 2000);
    };

    // 辅助函数 - 在 Map 中根据 Value 查找 Key
    const findMapKey = (map, valueToFind) => {
        if (!valueToFind) return null;
        const lowerCaseValue = valueToFind.toLowerCase();
        for (const [key, text] of Object.entries(map)) {
            if (text.toLowerCase() === lowerCaseValue || lowerCaseValue.includes(text.toLowerCase())) {
                return parseInt(key, 10);
            }
        }
        return null;
    };

    // 辅助函数 - 在字段数组中查找日期
    const findDateAndIndex = (fields, startIndex = 0) => {
        const dateRegex = /(\d{4})\.(\d{1,2})\.(\d{1,2})/;
        for (let i = startIndex; i < fields.length; i++) {
            const match = fields[i].match(dateRegex);
            if (match) {
                const newDate = new Date(`${match[1]}/${match[2]}/${match[3]} 19:30:00`);
                if (!isNaN(newDate.getTime())) {
                    return { date: newDate, index: i };
                }
            }
        }
        const defaultDate = new Date();
        defaultDate.setHours(19, 30, 0, 0);
        return { date: defaultDate, index: -1 };
    };


    // --- 智能填入逻辑处理器 (V5 - 最终修正版) ---
    const handleSmartFill = async () => {
        if (parts.length === 0) {
            setError("请先解析视频，再使用自动填入功能。");
            return;
        }

        const p1Title = parts[0].title;
        if (!p1Title) {
            setError("P1 标题为空，无法自动解析。");
            return;
        }

        // 核心修正：强制在括号周围添加空格，然后再移除括号
        let standardizedTitle = p1Title.replace(/】/g, '】 ').replace(/【/g, ' 【'); 
        standardizedTitle = standardizedTitle.replace(/[【】]/g, ''); 
        const titleFields = standardizedTitle.split(' ').filter(field => field.trim() !== ''); 

        if (titleFields.length === 0) {
            setError("标题解析失败，无法自动填入。");
            return;
        }

        const firstField = titleFields[0];
        let logicTemplate = null; // 'A' = 柚恩, 'B' = 露早
        let newOrgId = null;
        let isDuet = false; // --- 新增改动 1： 双播标志位 ---

        if (firstField.includes("柚恩&露早")) {
            logicTemplate = 'A'; newOrgId = 1; isDuet = true;
        } else if (firstField.includes("露早&柚恩")) {
            logicTemplate = 'B'; newOrgId = 2; isDuet = true;
        } else if (firstField.includes("柚恩")) {
            logicTemplate = 'A'; newOrgId = 1;
        } else if (firstField.includes("露早")) {
            logicTemplate = 'B'; newOrgId = 2;
        }

        if (!logicTemplate || !newOrgId) {
            setError("未能从标题识别出“露早”或“柚恩”，无法应用模板。");
            return;
        }
        
        setError(''); 

        let newAuthors = authors;
        let newDefaultAuthorId = commonData.authorId;

        if (newOrgId !== commonData.organizationId) { 
            try {
                newAuthors = await fetchAuthorsByOrganization(newOrgId);
                setAuthors(newAuthors); 
                if (newAuthors.length > 0) {
                    newDefaultAuthorId = newAuthors[0].id; 
                } else {
                    newDefaultAuthorId = ''; 
                }
            } catch (err) {
                setError("智能填入时获取作者列表失败: " + err.message);
                setAuthors([]);
                newDefaultAuthorId = '';
            }
        } else {
            // 如果社团ID没变，我们假设作者列表(authors)已经是正确的，只需设置默认作者
            if (authors.length > 0) {
                 newDefaultAuthorId = authors[0].id;
            } else {
                 newDefaultAuthorId = ''; // 以防万一
            }
        }


        // 准备所有计算数据
        let newPartsData = parts.map(p => ({ ...p }));
        let calculatedFormat = 6; // 默认 6 (其它)
        let calculatedDate = commonData.datetime;
        
        if (logicTemplate === 'A') {
            // --- [模板A: 柚恩] ---
            const formatKey = findMapKey(liveFormatMap, titleFields[1]);
            if (formatKey) calculatedFormat = formatKey;

            const { date, index: dateIndex } = findDateAndIndex(titleFields, 2); 
            calculatedDate = date;

            const p1TitlePieces = [];
            let formatFoundInTitle = false;
            if(dateIndex !== -1) {
                for (let i = dateIndex + 1; i < titleFields.length; i++) {
                    const field = titleFields[i];
                    if (!formatFoundInTitle && findMapKey(liveFormatMap, field)) {
                        formatFoundInTitle = true; 
                        continue; 
                    }
                    if (field.toLowerCase().match(/^p\d+$/)) {
                        break;
                    }
                    if (!formatFoundInTitle) {
                        p1TitlePieces.push(field);
                    }
                }
            }
            if(p1TitlePieces.length > 0) {
                 newPartsData[0].title = p1TitlePieces.join(' ');
            }


            // 其它P标题
            for (let i = 1; i < newPartsData.length; i++) {
                const partTitleStd = newPartsData[i].title.replace(/【/g, ' 【').replace(/】/g, '】 ');
                const partFields = partTitleStd.replace(/[【】]/g, '').split(' ').filter(Boolean);
                if (partFields.length > 0) {
                    newPartsData[i].title = partFields[partFields.length - 1];
                }
            }

        } else if (logicTemplate === 'B') {
            // --- [模板B: 露早] ---
            const { date, index: dateIndex } = findDateAndIndex(titleFields, 1);
            calculatedDate = date;

            const p1TitlePieces = [];
            let formatFoundFlag = false;
            if (dateIndex !== -1) {
                for (let i = dateIndex + 1; i < titleFields.length; i++) {
                    const field = titleFields[i];
                    if (field === "直播录像") continue;

                    const formatKey = findMapKey(liveFormatMap, field);
                    if (!formatFoundFlag && formatKey) {
                        calculatedFormat = formatKey; 
                        formatFoundFlag = true;
                        continue;
                    }

                    if (field.toLowerCase().match(/^p\d+$/)) {
                        break; 
                    }
                    
                    if (!formatFoundFlag) { 
                        p1TitlePieces.push(field);
                    }
                }
            }
             if(p1TitlePieces.length > 0) {
                 newPartsData[0].title = p1TitlePieces.join(' ');
            }

            // 其它P标题
            for (let i = 1; i < newPartsData.length; i++) {
                const partTitleStd = newPartsData[i].title.replace(/【/g, ' 【').replace(/】/g, '】 ');
                const partFields = partTitleStd.replace(/[【】]/g, '').split(' ').filter(Boolean);

                if (partFields.length >= 2) {
                    const last = partFields[partFields.length - 1];
                    const secondLast = partFields[partFields.length - 2];
                    newPartsData[i].title = `${secondLast} ${last}`;
                } else if (partFields.length === 1) {
                    newPartsData[i].title = partFields[0]; 
                }
            }
        }

        // 4. (关键) 一次性更新 Common Data
        setCommonData(prev => ({
            ...prev,
            organizationId: newOrgId,         
            authorId: newDefaultAuthorId,      
            // --- 新增改动 2： 应用双播优先 ---
            live_format: isDuet ? 5 : calculatedFormat, // 5 = 双播
            datetime: calculatedDate,        
            live_content: prev.live_content || 10 
        }));

        // 5. 更新 Parts 标题
        setParts(newPartsData);
    };


    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
             <Typography variant="h4" gutterBottom>Bilibili 视频导入工具</Typography>
            
            {/* --- 修正：修改顶部按钮布局 --- */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs={12} sm={8}>
                    <TextField fullWidth label="Bilibili 视频链接或 BV 号" value={biliInput} onChange={(e) => setBiliInput(e.target.value)} />
                </Grid>
                <Grid item xs={6} sm={2}>
                    <Button fullWidth variant="contained" onClick={handleParse} disabled={loading} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />} sx={{ height: '56px' }}>
                        {loading ? "解析中" : "解析"}
                    </Button>
                </Grid>
                
                {/* --- 新增：智能填入按钮(条件渲染) --- */}
                {parts.length > 0 && !loading && (
                    <Grid item xs={6} sm={2}>
                         <Button fullWidth variant="outlined" color="primary" onClick={handleSmartFill} startIcon={<AutoAwesomeIcon />} sx={{ height: '56px' }}>
                            智能填入
                        </Button>
                    </Grid>
                )}
            </Grid>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {parts.length > 0 && (
                <Box>
                    <Typography variant="h6" gutterBottom>通用信息 (将应用于所有分P)</Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} md={3}><FormControl fullWidth><InputLabel>所属社团</InputLabel><Select value={commonData.organizationId} label="所属社团" onChange={handleOrgChange}>{organizations.map(org => <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>)}</Select></FormControl></Grid>
                        <Grid item xs={6} md={3}><FormControl fullWidth required><InputLabel>作者</InputLabel><Select value={commonData.authorId} label="作者" onChange={(e) => setCommonData(p => ({ ...p, authorId: e.target.value }))}>{orgAuthors.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}</Select></FormControl></Grid>
                        <Grid item xs={6} md={3}><FormControl fullWidth required><InputLabel>直播形式</InputLabel><Select value={commonData.live_format} label="直播形式" onChange={(e) => setCommonData(p => ({ ...p, live_format: e.target.value }))}>{Object.entries(liveFormatMap).map(([id, text]) => <MenuItem key={id} value={parseInt(id)}>{text}</MenuItem>)}</Select></FormControl></Grid>
                        <Grid item xs={6} md={3}><FormControl fullWidth required><InputLabel>直播内容</InputLabel><Select value={commonData.live_content} label="直播内容" onChange={(e) => setCommonData(p => ({ ...p, live_content: e.target.value }))}>{Object.entries(liveContentMap).map(([id, text]) => <MenuItem key={id} value={parseInt(id)}>{text}</MenuItem>)}</Select></FormControl></Grid>
                        <Grid item xs={12}><LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}><DateTimePicker label="直播时间" value={commonData.datetime} onChange={handleDateChange} sx={{ width: '100%' }} views={['year', 'month', 'day', 'hours', 'minutes']} ampm={false} /></LocalizationProvider></Grid>
                    </Grid>

                    <Typography variant="h6" gutterBottom>视频分P列表</Typography>
                    {parts.map((p) => (
                        <Paper key={p.id} sx={{ p: 2, mb: 2 }} variant="outlined">
                            <Typography variant="subtitle1" gutterBottom>Part {p.part}</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="标题 (可修改)"
                                        value={p.title}
                                        onChange={(e) => handlePartDataChange(p.id, 'title', e.target.value)}
                                        multiline
                                        rows={2} 
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="封面 (可编辑)"
                                        value={p.cover}
                                        onChange={(e) => handlePartDataChange(p.id, 'cover', e.target.value)}
                                        helperText="不含 http:// 或 https:// 前缀"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            字幕: {p.subtitleSrt ? `${p.subtitleSrt.split('\n').length} 行` : '无字幕'}
                                        </Typography>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleOpenSubtitleEdit(p.id)}
                                        >
                                            {p.subtitleSrt ? '编辑字幕' : '添加字幕'}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Paper>
                    ))}
                    
                    {/* --- 移除底部的智能填入按钮，只保留提交按钮 --- */}
                    <Button variant="contained" color="success" startIcon={<SendIcon />} onClick={handlePreview} sx={{ mt: 2 }} fullWidth>
                        预览并提交
                    </Button>
                </Box>
            )}

            {/* --- 字幕编辑弹窗 --- */}
            <Dialog open={subtitleEditOpen} onClose={() => setSubtitleEditOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>
                    编辑字幕 - Part {parts.find(p => p.id === editingPartId)?.part}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        请输入或粘贴 SRT 格式的字幕内容
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={15}
                        value={editingSubtitle}
                        onChange={(e) => setEditingSubtitle(e.target.value)}
                        placeholder={"1\n00:00:00,000 --> 00:00:05,000\n这是第一行字幕\n\n2\n00:00:05,000 --> 00:00:10,000\n这是第二行字幕"}
                        sx={{ fontFamily: 'monospace' }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubtitleEditOpen(false)}>取消</Button>
                    <Button onClick={() => { setEditingSubtitle(''); }} color="error">清空</Button>
                    <Button onClick={handleSaveSubtitleEdit} variant="contained">保存</Button>
                </DialogActions>
            </Dialog>

            {/* --- Dialog 组件保持不变 --- */}
            <Dialog open={previewOpen} onClose={() => !subtitleUploadStatus.msg.includes('上传') && setPreviewOpen(false)} fullWidth maxWidth="md" scroll="paper">
                <DialogTitle>
                    {subtitleUploadStep ? '上传字幕' : '确认提交信息'}
                </DialogTitle>
                <DialogContent dividers>
                    {!subtitleUploadStep ? (
                        <Box>
                            {previewPayload?.map((item, index) => (
                                <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>Part {item.part}</Typography>
                                    <Typography variant="body1" gutterBottom fontWeight="bold">{item.title}</Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">作者</Typography>
                                            <Typography variant="body2">{authors.find(a => a.id === item.authorId)?.name || item.authorId}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">时间</Typography>
                                            <Typography variant="body2">{item.datetime.split(' ')[0]}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">形式</Typography>
                                            <Typography variant="body2">{liveFormatMap[item.live_format]}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">内容</Typography>
                                            <Typography variant="body2">{liveContentMap[item.live_content]}</Typography>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ mt: 2 }}>
                            <Typography>视频信息已上传，是否继续上传已解析的字幕？</Typography>
                            {subtitleUploadStatus.msg && (
                                <Box sx={{ width: '100%', mt: 2 }}>
                                     <LinearProgress variant="determinate" value={subtitleUploadProgress} />
                                </Box>
                            )}
                        </Box>
                    )}
                    {submissionStatus.msg && <Alert severity={submissionStatus.type} sx={{ mt: 2 }}>{submissionStatus.msg}</Alert>}
                    {subtitleUploadStep && subtitleUploadStatus.msg && <Alert severity={subtitleUploadStatus.type} sx={{ mt: 2 }}>{subtitleUploadStatus.msg}</Alert>}
                </DialogContent>
                <DialogActions>
                    {!subtitleUploadStep ? (
                        <>
                            <Button onClick={() => setPreviewOpen(false)}>返回修改</Button>
                            <Button onClick={handleConfirmUpload} variant="contained" disabled={submissionStatus.type === 'info'}>
                                {submissionStatus.type === 'info' ? <CircularProgress size={24}/> : '确认上传'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={() => { setPreviewOpen(false); resetAllState(); }}>稍后上传</Button>
                            <Button onClick={handleConfirmSubtitleUpload} variant="contained" startIcon={<CloudUploadIcon />} disabled={subtitleUploadStatus.msg.includes('上传')}>
                                {subtitleUploadStatus.msg.includes('上传中') ? '上传中...' : '上传字幕'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BiliBiliToolPage;